import { createParser } from "eventsource-parser";

/** A non-2xx answer to {@link fetchEventStream}, with its parsed JSON body. */
export class EventStreamHttpError extends Error {
  readonly status: number;
  /** The response body parsed as JSON, else null. */
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`Failed to fetch event stream, received HTTP ${status}`);
    this.name = "EventStreamHttpError";
    this.status = status;
    this.body = body;
  }
}

async function readJsonBody(response: Response): Promise<unknown> {
  try {
    const body: unknown = await response.json();
    return body;
  } catch {
    return null;
  }
}

export async function* fetchEventStream(
  url: string | URL,
  options: {
    headers?: Record<string, string>;
    body: unknown;
    signal?: AbortSignal;
    /**
     * Only for an endpoint that reads cookies. Bearer-authenticated callers
     * leave it unset: a credentialed cross-origin request is refused unless
     * the response allows credentials.
     */
    credentials?: RequestCredentials;
  },
): AsyncIterable<{ event: string | undefined; data: string }> {
  // Axios doesn't yet support fetch + streaming + CORS.
  const response = await fetch(url, {
    credentials: options.credentials,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...options.headers,
    },
    body: JSON.stringify(options.body),
    signal: options.signal,
  });

  if (!response.ok) {
    const body = await readJsonBody(response);
    throw new EventStreamHttpError(response.status, body);
  }

  // Get the response body as a ReadableStream
  const stream = response.body;
  if (!stream) {
    throw new Error("Expected response to have a body stream");
  }

  const reader = stream.getReader();
  const decoder = new TextDecoder();
  const buffer: { event: string | undefined; data: string }[] = [];
  const parser = createParser({
    onEvent: ({ event, data }) => {
      buffer.push({ event, data });
    },
  });

  // Read the stream
  // Don't use pipes and transform streams since they're not yet supported on iOS.
  while (true) {
    const { done, value } = await reader.read();
    options.signal?.throwIfAborted();
    if (done) {
      break;
    }

    parser.feed(decoder.decode(value));
    yield* buffer;
    buffer.length = 0;
  }
}
